/**
 * #4974 INSTALLPROMPT: what the install bar (AppShell) and the "Install the
 * app" menu item (UserMenu) both read.
 *
 * Edge and Chrome fire `beforeinstallprompt` once per page load when the app
 * can be installed, often before the signed-in shell has mounted. One listener,
 * attached at boot (main.tsx), holds that event here for the bar and the menu
 * item, and records `appinstalled` on this device. Two facts are per device in
 * localStorage, because an install is per device: when Not now was last
 * pressed, and that the app was installed here. Every read and write is
 * guarded: blocked storage shows the bar as if nothing was stored, and a
 * re-read that throws keeps what this page load already knows. Another tab's
 * write reaches this one through the `storage` event, the station kiosk mark
 * included (from the event's own newValue when the re-read throws), and a
 * fresh `beforeinstallprompt` in a browser tab clears "installed" (the app
 * was removed).
 *
 * Telemetry (#1876 feature-usage beacon), each event with meta.display_mode:
 *  - install_bar_shown: the bar stayed on one page for a second (or was
 *    answered sooner), once per page load per kind, with that page's tab.
 *  - install_accepted: the browser's install dialog answered "accepted", or
 *    the person opened the /install steps (Show me how, or the menu link).
 *  - install_dismissed: Not now, or the browser's dialog answered "dismissed".
 */
import { useSyncExternalStore } from "react";
import { useLocation } from "react-router";
import { logUsageEvent } from "./telemetry-events";
import { readStandalone, type InstallPlatform } from "./install-platform";
import { isUnattendedRoute, useIsUnattendedSurface } from "./unattended-session";
import { KIOSK_KEY, onKioskMarkChange } from "./station-client";
import { usePinSession, useUserRole } from "@/hooks/use-rbac";

/** Chromium's install event. Not in lib.dom, so typed here. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<unknown>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform?: string }>;
}

export const INSTALL_DISMISSED_KEY = "crosbe-install-dismissed-at";
export const INSTALL_INSTALLED_KEY = "crosbe-install-installed";
/** Not now hides the bar on this device for this long. */
export const INSTALL_DISMISS_MS = 30 * 24 * 60 * 60 * 1000;
/**
 * The bar counts as seen once it has stayed on one page this long. By then a
 * redirect from / (a phone to /analyst, a station or sales landing) has run,
 * so install_bar_shown carries the page the bar is shown on.
 */
export const INSTALL_SHOWN_AFTER_MS = 1000;

export interface InstallPromptState {
  /** The held `beforeinstallprompt`, until it is used. */
  event: BeforeInstallPromptEvent | null;
  /** The browser's dialog was opened on this page load (an event prompts once). */
  answered: boolean;
  /** `appinstalled` was seen on this device. */
  installed: boolean;
  /** When Not now was pressed on this device (epoch ms), or null. */
  dismissedAt: number | null;
  /** This device carries the station kiosk mark (station-client.ts). */
  kiosk: boolean;
}

const INITIAL: InstallPromptState = { event: null, answered: false, installed: false, dismissedAt: null, kiosk: false };
let state: InstallPromptState = INITIAL;
const listeners = new Set<() => void>();
const attached = new WeakSet<object>();
const shownLogged = new Set<string>();
let kioskWatched = false;

function setState(next: Partial<InstallPromptState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The stored value, or undefined when the read throws (blocked storage). */
function readKey(key: string): string | null | undefined {
  try {
    return localStorage.getItem(key);
  } catch {
    return undefined;
  }
}

/**
 * This device's stored answers and kiosk mark. A read that throws is not a
 * read that found nothing: its field is left out, so the state keeps what
 * this page load already knows (a Not now or `appinstalled` seen here).
 */
function readStoredAnswers(): Partial<InstallPromptState> {
  const next: Partial<InstallPromptState> = {};
  const installed = readKey(INSTALL_INSTALLED_KEY);
  if (installed !== undefined) next.installed = installed === "1";
  const dismissed = readKey(INSTALL_DISMISSED_KEY);
  if (dismissed !== undefined) {
    const at = dismissed === null ? NaN : Number(dismissed);
    next.dismissedAt = Number.isFinite(at) ? at : null;
  }
  const kiosk = readKey(KIOSK_KEY);
  if (kiosk !== undefined) next.kiosk = kiosk === "1";
  return next;
}

/**
 * #5093: the answers after another tab's `storage` event on `key`. Storage
 * is re-read; when the re-read of that key throws, its field takes the
 * event's own newValue (null: the key was removed), so the other tab's write
 * still lands. A cleared storage (key null) whose re-read throws clears
 * nothing.
 */
function answersAfterStorageEvent(key: string | null, newValue: string | null): Partial<InstallPromptState> {
  const next = readStoredAnswers();
  if (key === INSTALL_INSTALLED_KEY && next.installed === undefined) next.installed = newValue === "1";
  if (key === INSTALL_DISMISSED_KEY && next.dismissedAt === undefined) {
    const at = newValue === null ? NaN : Number(newValue);
    next.dismissedAt = Number.isFinite(at) ? at : null;
  }
  if (key === KIOSK_KEY && next.kiosk === undefined) next.kiosk = newValue === "1";
  return next;
}

/**
 * Attach the app's one `beforeinstallprompt`, `appinstalled` and `storage`
 * listener and read this device's stored answers. Called once from main.tsx
 * before the first render; a second call on the same target does nothing.
 */
export function listenForInstallPrompt(target: EventTarget | undefined = typeof window === "undefined" ? undefined : window): void {
  if (!target || attached.has(target)) return;
  attached.add(target);
  setState(readStoredAnswers());
  // /station marking or clearing this device, in this tab.
  if (!kioskWatched) {
    kioskWatched = true;
    onKioskMarkChange(() => setState(readStoredAnswers()));
  }
  target.addEventListener("beforeinstallprompt", (e) => {
    // Held, so the browser shows no bar of its own and ours can call prompt().
    e.preventDefault();
    // Inside the installed app an offer says nothing about this device's
    // install, so the stored "installed" stays.
    if (readStandalone()) {
      setState({ event: e as BeforeInstallPromptEvent });
      return;
    }
    // In a browser tab a fresh offer means the app is not installed here (it
    // was removed), so the stored "installed" is stale. Not now still holds.
    try {
      localStorage.removeItem(INSTALL_INSTALLED_KEY);
    } catch {
      // blocked storage: cleared for this page load only
    }
    setState({ event: e as BeforeInstallPromptEvent, installed: false });
  });
  target.addEventListener("appinstalled", () => {
    try {
      localStorage.setItem(INSTALL_INSTALLED_KEY, "1");
    } catch {
      // blocked storage: the bar hides for this page load only
    }
    setState({ event: null, installed: true });
  });
  // Not now, an install or a kiosk mark in another tab on this device. A null
  // key is that tab clearing storage.
  target.addEventListener("storage", (e) => {
    const { key, newValue } = e as StorageEvent;
    if (key === null || key === INSTALL_DISMISSED_KEY || key === INSTALL_INSTALLED_KEY || key === KIOSK_KEY) {
      setState(answersAfterStorageEvent(key, newValue));
    }
  });
}

export interface InstallPlace {
  kiosk: boolean;
  unattended: boolean;
  pinSession: boolean;
}

/**
 * Where an install may be offered at all, shared by the bar and the menu
 * item: never on a station kiosk (this device's mark), in a PIN session (the
 * server's signal, which holds when blocked storage makes the mark read
 * false), or on an unattended screen (its route, or a mounted surface).
 */
export function installAllowedHere({ kiosk, unattended, pinSession }: InstallPlace): boolean {
  return !kiosk && !unattended && !pinSession;
}

/**
 * installAllowedHere for this page, or null until the role read answers (a
 * PIN session is not known before it). The route is checked during render:
 * an unattended page registers only after it paints, and its chunk may still
 * be loading.
 */
export function useInstallAllowedHere(): boolean | null {
  const { pathname } = useLocation();
  const unattended = useIsUnattendedSurface() || isUnattendedRoute(pathname);
  const roleKnown = useUserRole().data !== undefined;
  const pinSession = usePinSession() !== null;
  const { kiosk } = useInstallPrompt();
  if (!roleKnown) return null;
  return installAllowedHere({ kiosk, unattended, pinSession });
}

export function useInstallPrompt(): InstallPromptState {
  return useSyncExternalStore(subscribe, () => state, () => INITIAL);
}

export function displayMode(): "standalone" | "browser" {
  return readStandalone() ? "standalone" : "browser";
}

export type InstallFrom = "bar" | "menu";

export function logInstallEvent(
  eventType: "install_bar_shown" | "install_accepted" | "install_dismissed",
  meta: Record<string, string>,
  tab?: string,
): void {
  try {
    logUsageEvent({ eventType, ...(tab ? { tab } : {}), meta: { display_mode: displayMode(), ...meta } });
  } catch {
    // Telemetry must never interrupt the application.
  }
}

/**
 * install_bar_shown, at most once per page load for each kind of bar. The tab
 * is passed in (the page the bar is shown on): a row with no tab is left out
 * of the Admin summary.
 */
export function logInstallBarShown(kind: InstallBarKind, tab: string): void {
  if (shownLogged.has(kind)) return;
  shownLogged.add(kind);
  logInstallEvent("install_bar_shown", { bar: kind }, tab);
}

/**
 * Open the browser's own install dialog with the held event. Called straight
 * from a click, so prompt() runs inside the click (the browser requires it).
 * Returns the person's answer, or null when no event is held.
 */
export async function promptInstall(from: InstallFrom): Promise<"accepted" | "dismissed" | null> {
  const event = state.event;
  if (!event) return null;
  setState({ event: null, answered: true });
  try {
    await event.prompt();
    const choice = await event.userChoice;
    const outcome = choice?.outcome === "accepted" ? "accepted" : "dismissed";
    logInstallEvent(outcome === "accepted" ? "install_accepted" : "install_dismissed", { from, via: "prompt" });
    return outcome;
  } catch {
    return null;
  }
}

/** Not now: hide the bar on this device for 30 days. */
export function dismissInstallBar(now: number = Date.now()): void {
  try {
    localStorage.setItem(INSTALL_DISMISSED_KEY, String(now));
  } catch {
    // blocked storage: hidden for this page load only
  }
  setState({ dismissedAt: now });
  logInstallEvent("install_dismissed", { from: "bar", via: "not_now" });
}

export type InstallBarKind = "prompt" | "steps";

export interface InstallBarInputs {
  signedIn: boolean;
  standalone: boolean;
  /** installAllowedHere, false while it is not yet known. */
  allowed: boolean;
  platform: InstallPlatform | null;
  prompt: InstallPromptState;
  now: number;
}

/**
 * Which bar to show, or null. Shown only when every one of these holds:
 * signed in, not running installed, allowed here (installAllowedHere), not
 * installed or dismissed (within the last 30 days) on this device, and
 * either the browser offered an install (the event) or the device is a phone
 * whose steps /install shows. Desktop Safari and Firefox fire no event, so
 * they get no bar.
 */
export function installBarKind({ signedIn, standalone, allowed, platform, prompt, now }: InstallBarInputs): InstallBarKind | null {
  if (!signedIn || standalone || !allowed) return null;
  if (prompt.installed || prompt.answered) return null;
  // A dismissal counts only when it is in the past: a time ahead of the clock
  // (one set forward, or 1e99) is no dismissal.
  if (prompt.dismissedAt !== null) {
    const age = now - prompt.dismissedAt;
    if (age >= 0 && age < INSTALL_DISMISS_MS) return null;
  }
  if (prompt.event) return "prompt";
  if (platform === "ios" || platform === "android") return "steps";
  return null;
}

/**
 * The menu item stays after Not now. Hidden where the bar is never allowed,
 * inside the installed app, and once this device has installed it (a fresh
 * offer clears that, so it comes back if the app is removed).
 */
export function showInstallMenuItem({ standalone, allowed, prompt }: { standalone: boolean; allowed: boolean | null; prompt: InstallPromptState }): boolean {
  return allowed === true && !standalone && !prompt.installed;
}

/**
 * Test helper: forget the held event, the stored answers read at boot and the
 * shown log, as a reload does. A reload is a new window, so a test then passes
 * a fresh target to listenForInstallPrompt.
 */
export function resetInstallPromptForTests(): void {
  state = INITIAL;
  shownLogged.clear();
  for (const listener of listeners) listener();
}
